import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ClientLoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    clientId: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Use XMLHttpRequest instead of fetch to avoid rrweb-recorder interference
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/client/login`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true; // Important: include cookies
    
    xhr.onload = function() {
      setLoading(false);
      
      try {
        const data = JSON.parse(xhr.responseText);
        
        // Success case
        if (xhr.status === 200 && data.ok === true) {
          navigate(`/client-dashboard/${formData.clientId}`);
          return;
        }
        
        // Handle error responses (401, 429, etc.)
        if (data.error) {
          switch (data.error) {
            case 'USER_NOT_FOUND':
              setError("We couldn't find that Client ID. Please check that you entered your Client ID exactly as provided in your welcome email.");
              break;
            
            case 'INVALID_PASSWORD':
              setError("Incorrect password. Please double-check your password and try again.");
              break;
            
            case 'LOCKED_OUT':
              const managerEmail = data.manager_email || 'intake@bm-assist.co';
              setError(`Too many login attempts. For your security, your account is temporarily locked. Please contact your Project Manager at ${managerEmail} to reset your password.`);
              break;
            
            case 'NO_PASSWORD':
              setError("No password has been set for your account. Please contact your Project Manager to receive your login credentials.");
              break;
            
            case 'LOGIN_DISABLED':
              setError("Your account access has been disabled. Please contact your Project Manager for assistance.");
              break;
            
            case 'SERVER_ERROR':
              setError("Something went wrong while logging you in. Please try again in a moment. If this continues, email intake@bm-assist.co");
              break;
            
            default:
              setError("Login failed. Please check your credentials and try again.");
          }
        } else {
          setError("Login failed. Please try again.");
        }
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        setError("Something went wrong while logging you in. Please try again in a moment. If this continues, email intake@bm-assist.co");
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      console.error('Network error during login');
      setError("Something went wrong while logging you in. Please try again in a moment. If this continues, email intake@bm-assist.co");
    };
    
    xhr.send(JSON.stringify({
      client_id: formData.clientId,
      password: formData.password
    }));
  };

  return (
    <div className="page-transition" style={{
      minHeight: '100vh',
      background: '#F8F9FA',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: '#FFFFFF',
        borderRadius: '12px',
        padding: '48px',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            color: '#0A0A0A',
            fontSize: '24px',
            fontWeight: '600',
            marginBottom: '6px',
            letterSpacing: '-0.02em'
          }}>
            BM-Assist
          </h1>
          <p style={{
            color: '#6B7280',
            fontSize: '14px',
            margin: 0,
            fontWeight: '400'
          }}>
            Client Portal
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px'
          }}>
            <AlertCircle size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} strokeWidth={2} />
            <span style={{ color: '#991B1B', fontSize: '13px', lineHeight: '1.5' }}>{error}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Client ID Input */}
          <div>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '13px',
              fontWeight: '500',
              marginBottom: '8px',
              letterSpacing: '-0.01em'
            }}>
              Client ID
            </label>
            <input
              type="text"
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              placeholder="e.g., CP-123456"
              required
              data-testid="client-id-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: '#FFFFFF',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                color: '#0A0A0A',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3B82F6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Password Input */}
          <div>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '13px',
              fontWeight: '500',
              marginBottom: '8px',
              letterSpacing: '-0.01em'
            }}>
              Password
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Enter your password"
              required
              data-testid="password-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: '#FFFFFF',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                color: '#0A0A0A',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3B82F6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            style={{
              width: '100%',
              padding: '11px',
              background: loading ? '#93C5FD' : '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
              marginTop: '4px',
              letterSpacing: '-0.01em'
            }}
            onMouseOver={(e) => !loading && (e.target.style.background = '#2563EB')}
            onMouseOut={(e) => !loading && (e.target.style.background = '#3B82F6')}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {/* Help Text */}
        <div style={{
          marginTop: '24px',
          paddingTop: '20px',
          borderTop: '1px solid #E5E7EB'
        }}>
          <p style={{
            color: '#6B7280',
            fontSize: '12px',
            margin: 0,
            lineHeight: '1.5'
          }}>
            Contact your project manager if you don't have your login credentials.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientLoginPage;
